import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import notification from 'utils/notificationBridge';
import { signOut } from 'store/slices/authSlice';
import {
  markListStaleForContentType,
  removePublishJob,
  updatePublishJobProgress,
} from 'redux/actions/PendingPublish';
import { clearJobFiles, getJobFiles } from 'helpers/publishJobFiles';
import { runPublishJob } from 'helpers/runPublishJob';
import { PUBLISH_CONTENT_TYPES } from 'helpers/publishContentTypes';
import { hasSingleImageFiles } from 'helpers/stmarysPublishJobs';
import {
  closeContentPublishNotification,
  getPublishFailedMessage,
  getPublishSuccessMessage,
  openContentPublishNotification,
} from 'helpers/contentPublishNotification';

const SINGLE_IMAGE_TYPES = new Set([
  PUBLISH_CONTENT_TYPES.ADMINISTRATION,
  PUBLISH_CONTENT_TYPES.PARISH_PRIEST,
  PUBLISH_CONTENT_TYPES.LEADERSHIP_HISTORY,
  PUBLISH_CONTENT_TYPES.HOUSE,
  PUBLISH_CONTENT_TYPES.HISTORY,
  PUBLISH_CONTENT_TYPES.HIERARCHY,
  PUBLISH_CONTENT_TYPES.ORGANIZATION,
  PUBLISH_CONTENT_TYPES.MEMBERS,
  PUBLISH_CONTENT_TYPES.OCCASION,
  PUBLISH_CONTENT_TYPES.EVENTS,
  PUBLISH_CONTENT_TYPES.FESTIVALS,
  PUBLISH_CONTENT_TYPES.QUOTES,
  PUBLISH_CONTENT_TYPES.INCOME_PDF_LINK,
]);

const IMAGE_DTO_TYPES = new Set([
  PUBLISH_CONTENT_TYPES.CHURCH,
  PUBLISH_CONTENT_TYPES.CHAPEL,
  PUBLISH_CONTENT_TYPES.ADVERTISEMENT,
  PUBLISH_CONTENT_TYPES.NEWS,
]);

const PAYLOAD_ONLY_TYPES = new Set([
  PUBLISH_CONTENT_TYPES.TEMPLATE,
  PUBLISH_CONTENT_TYPES.CATEGORIES,
  PUBLISH_CONTENT_TYPES.WIDGETS,
  PUBLISH_CONTENT_TYPES.SEO,
  PUBLISH_CONTENT_TYPES.INFORMATION_POLICY,
  PUBLISH_CONTENT_TYPES.CONFIG_SETTINGS,
  PUBLISH_CONTENT_TYPES.ADMIN_SETTINGS,
  PUBLISH_CONTENT_TYPES.OFFICIAL_CONTACT,
  PUBLISH_CONTENT_TYPES.LIVE_STREAMING,
  PUBLISH_CONTENT_TYPES.POSITION,
]);

const hasRequiredFiles = (job, files) => {
  switch (job.contentType) {
    case PUBLISH_CONTENT_TYPES.BANNER:
      if (job.mode === 'EDIT') {
        const hasWeb = files?.webFile || files?.existingWebImage;
        const hasMobile = files?.mobileFile || files?.existingMobileImage;
        return Boolean(hasWeb && hasMobile);
      }
      return Boolean(files?.webFile && files?.mobileFile);
    case PUBLISH_CONTENT_TYPES.PAGES:
      return Array.isArray(files?.imageItems) && files.imageItems.length > 0;
    case PUBLISH_CONTENT_TYPES.GALLERY: {
      // Edit updates gallery metadata only; media is managed separately.
      if (job.mode === 'EDIT') {
        return true;
      }

      const galleryType = job.payload?.type || 'Image';
      const galleryImages = (files?.galleryImages || []).filter(
        (image) => image?.file || image?.existingUrl,
      );
      const galleryVideos = files?.galleryVideos || [];
      const galleryAudios = files?.galleryAudios || [];
      const videoType =
        job.payload?.videos?.[0]?.videoType ||
        galleryVideos[0]?.videoType ||
        'Youtube';
      const isUploadVideoType =
        videoType === 'Uploaded' || videoType === 'Upload';
      const audioType =
        job.payload?.audio?.[0]?.audioType ||
        galleryAudios[0]?.audioType ||
        'Uploaded';
      const isUploadAudioType =
        audioType === 'Uploaded' || audioType === 'Upload';
      const hasYoutubeVideos =
        !isUploadVideoType && (job.payload?.videos || []).length > 0;
      const hasUploadVideos = isUploadVideoType && galleryVideos.length > 0;
      const hasYoutubeAudios =
        !isUploadAudioType && (job.payload?.audio || []).length > 0;
      const hasUploadAudios =
        isUploadAudioType &&
        galleryAudios.some((audio) => audio?.file || audio?.existingUrl);
      const hasImages = galleryImages.length > 0;
      const hasVideos = hasUploadVideos || hasYoutubeVideos;
      const hasAudios = hasUploadAudios || hasYoutubeAudios;

      if (galleryType === 'Image') {
        if (!hasImages) {
          return false;
        }
        return galleryImages.some((image) => image?.mainImage === 'Yes');
      }

      if (galleryType === 'Video') {
        if (!hasVideos) {
          return false;
        }
        if (hasUploadVideos) {
          return galleryVideos.every((video) => video?.file || video?.existingUrl);
        }
        return true;
      }

      if (galleryType === 'Audio') {
        if (!hasAudios) {
          return false;
        }
        if (hasUploadAudios) {
          return galleryAudios.every(
            (audio) => audio?.file || audio?.existingUrl,
          );
        }
        return true;
      }

      return false;
    }
    default:
      break;
  }

  if (PAYLOAD_ONLY_TYPES.has(job.contentType)) {
    return true;
  }

  if (
    job.contentType === PUBLISH_CONTENT_TYPES.ADMINISTRATION ||
    job.contentType === PUBLISH_CONTENT_TYPES.MEMBERS
  ) {
    return true;
  }

  if (SINGLE_IMAGE_TYPES.has(job.contentType) || IMAGE_DTO_TYPES.has(job.contentType)) {
    return hasSingleImageFiles(job, files);
  }

  return false;
};

export const useContentPublishQueue = () => {
  const dispatch = useDispatch();
  const jobs = useSelector((state) => state.pendingPublish.jobs);
  const pendingJobId = jobs.find((job) => job.status === 'pending')?.id;
  const accessToken = useSelector((state) => state.auth.token);
  const activeJobIdRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!pendingJobId || !accessToken) {
      return;
    }

    if (activeJobIdRef.current) {
      return;
    }

    const pendingJob = jobs.find((job) => job.id === pendingJobId);
    if (!pendingJob) {
      return;
    }

    const files = getJobFiles(pendingJob.id);
    if (!hasRequiredFiles(pendingJob, files)) {
      dispatch(removePublishJob(pendingJob.id));
      clearJobFiles(pendingJob.id);
      notification.error({
        title: 'Error',
        description: 'Required files are missing.',
      });
      return;
    }

    activeJobIdRef.current = pendingJob.id;
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const showProgress = (phase, percent) => {
      openContentPublishNotification({
        jobId: pendingJob.id,
        title: pendingJob.title,
        contentType: pendingJob.contentType,
        phase,
        percent,
      });
    };

    dispatch(
      updatePublishJobProgress({
        id: pendingJob.id,
        status: 'processing',
        phase: 'queued',
        percent: 0,
      }),
    );
    showProgress('queued', 0);

    runPublishJob({
      job: pendingJob,
      files,
      accessToken,
      signal: controller.signal,
      onProgress: ({ phase, percent, status }) => {
        dispatch(
          updatePublishJobProgress({
            id: pendingJob.id,
            phase,
            percent,
            status: status || 'processing',
          }),
        );
        showProgress(phase, percent);
      },
    })
      .then(() => {
        closeContentPublishNotification(pendingJob.id);
        clearJobFiles(pendingJob.id);
        dispatch(removePublishJob(pendingJob.id));
        dispatch(markListStaleForContentType(pendingJob.contentType));
        notification.success({
          title: 'Success',
          description: getPublishSuccessMessage(
            pendingJob.contentType,
            pendingJob.mode,
          ),
        });
      })
      .catch((error) => {
        if (error?.name === 'AbortError') {
          closeContentPublishNotification(pendingJob.id);
          return;
        }

        closeContentPublishNotification(pendingJob.id);

        if (error?.status === 401 || error?.statusCode === 401) {
          dispatch(signOut());
          return;
        }

        clearJobFiles(pendingJob.id);
        dispatch(removePublishJob(pendingJob.id));
        notification.error({
          title: 'Error',
          description:
            error?.message ||
            getPublishFailedMessage(pendingJob.contentType, pendingJob.mode),
        });
      })
      .finally(() => {
        if (activeJobIdRef.current === pendingJob.id) {
          activeJobIdRef.current = null;
        }
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      });
  }, [accessToken, dispatch, jobs, pendingJobId]);
};
